import React, { useState, useEffect, KeyboardEvent } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Tooltip,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import { useAppSelector, useAppDispatch } from "@store/hooks";
import {
  closeResourceManagementModal,
  setPaperEditorMode,
} from "@store/slices/viewSlice";
import { selectPaper } from "@store/thunks/workspaceThunks";
import {
  loadAllWorkspacePapers,
  renamePaper,
  deletePaper,
  selectAllWorkspacePapers,
  selectWorkspacePapersLoading,
  selectWorkspacePapersError,
} from "@features/workspace-papers/state/workspacePapersSlice";
import { selectIsEvaluator } from "@store/selectors";
import EmptyState from "@shared/components/states/EmptyState";
import { CreatePaperModal } from "./CreatePaperModal";
import type { RootState } from "@store/index";

export const PapersTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const papers = useAppSelector(selectAllWorkspacePapers);
  const loading = useAppSelector(selectWorkspacePapersLoading);
  const error = useAppSelector(selectWorkspacePapersError);
  const isEvaluator = useAppSelector(selectIsEvaluator);
  const workspaceId = useAppSelector(
    (state: RootState) => state.workspace.workspace_id
  );

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (workspaceId) {
      dispatch(loadAllWorkspacePapers(workspaceId));
    }
  }, [workspaceId, dispatch]);

  const handleView = (paperId: string) => {
    dispatch(selectPaper(paperId));
    dispatch(setPaperEditorMode("modal"));
    dispatch(closeResourceManagementModal());
  };

  const startEdit = (paperId: string, currentName: string) => {
    setEditingId(paperId);
    setEditValue(currentName);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const commitEdit = async (paperId: string) => {
    const next = editValue.trim();
    const current = papers.find((p) => p.paper_id === paperId);
    if (!current || !next || next === current.paper_name) {
      cancelEdit();
      return;
    }
    setSavingId(paperId);
    try {
      await dispatch(renamePaper({ paperId, paperName: next })).unwrap();
    } catch (err) {
      console.error("Failed to rename paper:", err);
      alert("Failed to rename paper. Please try again.");
    } finally {
      setSavingId(null);
      cancelEdit();
    }
  };

  const handleEditKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    paperId: string
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEdit(paperId);
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  };

  const handleDelete = async (paperId: string) => {
    const paper = papers.find((p) => p.paper_id === paperId);
    if (!paper) return;
    if (
      !window.confirm(
        `Delete "${paper.paper_name || "Untitled Paper"}"? This action cannot be undone.`
      )
    ) {
      return;
    }
    setDeletingId(paperId);
    try {
      await dispatch(deletePaper(paperId)).unwrap();
    } catch (err) {
      console.error("Failed to delete paper:", err);
      alert("Failed to delete paper. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading && papers.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (papers.length === 0) {
    return (
      <>
        <EmptyState
          icon={
            <EditNoteOutlinedIcon
              sx={{ fontSize: 64, color: "text.secondary" }}
            />
          }
          title="No papers yet"
          description="Draft papers to synthesize your research. Start by creating a new paper."
          actionLabel="Create Paper"
          onAction={() => setCreateModalOpen(true)}
        />
        <CreatePaperModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          workspaceId={workspaceId}
        />
      </>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Workspace Papers</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
        >
          Create Paper
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Created</TableCell>
              {isEvaluator && (
                <>
                  <TableCell>Assigned By</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Required</TableCell>
                </>
              )}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {papers.map((paper) => {
              const isEditing = editingId === paper.paper_id;
              const isSaving = savingId === paper.paper_id;
              const isDeleting = deletingId === paper.paper_id;
              const isOverdue =
                paper.due_date && new Date(paper.due_date) < new Date();
              return (
                <TableRow key={paper.paper_id} hover>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => handleEditKeyDown(e, paper.paper_id)}
                        onBlur={() => commitEdit(paper.paper_id)}
                        autoFocus
                        disabled={isSaving}
                        inputProps={{ maxLength: 255 }}
                        fullWidth
                      />
                    ) : (
                      <>
                        <Typography variant="body1" fontWeight={500}>
                          {paper.paper_name || "Untitled Paper"}
                        </Typography>
                        {paper.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                          >
                            {paper.description}
                          </Typography>
                        )}
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(paper.created_at)}
                    </Typography>
                  </TableCell>
                  {isEvaluator && (
                    <>
                      <TableCell>
                        {paper.assigned_by_username || "—"}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={isOverdue ? "error" : "text.primary"}
                        >
                          {paper.due_date ? formatDate(paper.due_date) : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={paper.required ? "Required" : "Optional"}
                          size="small"
                          color={paper.required ? "error" : "default"}
                          variant={paper.required ? "filled" : "outlined"}
                        />
                      </TableCell>
                    </>
                  )}
                  <TableCell align="right">
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Tooltip title="Open paper">
                        <span>
                          <IconButton
                            size="small"
                            color="inherit"
                            onClick={() => handleView(paper.paper_id)}
                            disabled={isEditing}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Rename">
                        <span>
                          <IconButton
                            size="small"
                            color="inherit"
                            onClick={() =>
                              startEdit(paper.paper_id, paper.paper_name || "")
                            }
                            disabled={isEditing}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(paper.paper_id)}
                            disabled={isDeleting || isEditing}
                          >
                            {isDeleting ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <CreatePaperModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        workspaceId={workspaceId}
      />
    </Box>
  );
};
